import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoon, faSun, faFeather, faCheck } from '@fortawesome/free-solid-svg-icons';
import { THEMES, useTheme } from '../contexts/ThemeContext';

const ICONS = {
  dark: faMoon,
  light: faSun,
  robinhood: faFeather,
};

const SWATCH = {
  dark: ['#0B0E14', '#3DDC97', '#6C8CFF'],
  light: ['#F3F5F8', '#0F9F6E', '#3D5AFE'],
  robinhood: ['#000000', '#00C805', '#FF5000'],
};

/**
 * Appearance picker. Compact = header icon + popover. Full = settings cards.
 */
const ThemePicker = ({ compact = false }) => {
  const { theme, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (!compact) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {THEMES.map((item) => {
          const active = theme === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => setTheme(item.id)}
              className={`text-left rounded-lg border px-3 py-3 transition-colors ${
                active ? 'border-action bg-elevated' : 'border-hairline hover:bg-elevated'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="inline-flex items-center gap-2 text-sm text-ink">
                  <FontAwesomeIcon icon={ICONS[item.id]} className="text-xs text-muted" />
                  {item.label}
                </span>
                {active && <FontAwesomeIcon icon={faCheck} className="text-[10px] text-action" />}
              </div>
              <div className="mt-2 flex gap-1">
                {(SWATCH[item.id] || []).map((c) => (
                  <span key={c} className="h-3 flex-1 rounded-sm" style={{ background: c }} />
                ))}
              </div>
              <div className="mt-2 text-[10px] text-muted">{item.hint}</div>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="h-11 w-11 sm:h-8 sm:w-8 inline-flex items-center justify-center rounded-md border border-hairline text-muted hover:bg-elevated hover:text-ink"
        title={`Theme: ${THEMES.find((t) => t.id === theme)?.label || theme}`}
        aria-label="Appearance"
      >
        <FontAwesomeIcon icon={ICONS[theme] || faMoon} className="text-xs" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-56 lt-card z-50 overflow-hidden p-1.5">
          <div className="px-2 py-1.5 lt-label">Appearance</div>
          {THEMES.map((item) => {
            const active = theme === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  setTheme(item.id);
                  setOpen(false);
                }}
                className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-md text-left text-sm ${
                  active ? 'bg-elevated text-ink' : 'text-muted hover:bg-elevated hover:text-ink'
                }`}
              >
                <FontAwesomeIcon icon={ICONS[item.id]} className="text-[11px] w-3.5" />
                <span className="flex-1">{item.label}</span>
                <span className="flex gap-0.5">
                  {(SWATCH[item.id] || []).map((c) => (
                    <span key={c} className="h-2.5 w-2.5 rounded-full border border-hairline" style={{ background: c }} />
                  ))}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ThemePicker;
